import React, { useMemo } from "react";
import Pagination from "../reusable/Pagination";
import BlueDot from "../../assets/svg/BlueDot.svg";
import GrayDot from "../../assets/svg/GrayDot.svg";
import OrangeDot from "../../assets/svg/OrangeDot.svg";
import Loader from "../../components/Live/Loader";
import { useNavigate } from "react-router-dom";
import { MdDeleteOutline, MdCheckCircle, MdCancel } from "react-icons/md";
import { FiEdit, FiRefreshCw } from "react-icons/fi";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import ActionsDropdown from "../reusable/ActionsDropdown";
import { useSelector, useDispatch } from "react-redux";
import { selectUserInformations } from "../../features/authSlice";
import { hasPermission, hasRole, showToast } from "../../utils/utils";
import { API } from "../../shared/api/Api";
import axios from "axios";

const PlacementTestTable = ({
  activeTab,
  setActiveTab,
  placementTests,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  loader,
  setReFetchData,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const determinedTableTabs = useMemo(() => {
    let tabs = ["All Tests"];
    if (user.student) {
      tabs.push("My Tests");
    }
    return tabs;
  }, [user]);

  const columns = [
    { id: "title", label: "Test Name", sortable: true },
    { id: "price", label: "Price", sortable: true },
    { id: "start_date", label: "Start Date", sortable: true },
    { id: "duration_minutes", label: "Duration", sortable: true },
    { id: "status", label: "Status", sortable: true },
  ];

  if (activeTab === "My Tests") {
    columns.push({ id: "approval_status", label: "Approval", sortable: false });
    columns.push({ id: "payment_status", label: "Payment", sortable: false });
    columns.push({ id: "score", label: "Score", sortable: false });
  }

  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;
    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp className={`${isActive && currentOrderDir === "ASC" ? "text-primary" : "text-gray-400"} -mb-1`} size={12} />
        <FaSortDown className={`${isActive && currentOrderDir === "DESC" ? "text-primary" : "text-gray-400"} -mt-1`} size={12} />
      </div>
    );
  };

  const getDropdownActions = (id, testData) => {
    const actions = [];

    if (hasPermission(user, "update_placement_test")) {
      actions.push({
        icon: <FiEdit className="w-4 h-4" />,
        label: "Edit",
        onClick: () => navigate(`/dashboard/placement-tests/${id}/edit`),
      });
    }

    if (hasPermission(user, "delete_placement_test")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: async () => {
          if (window.confirm("Are you sure?")) {
            try {
              const { data } = await axios.delete(`${API.PlacementTests.delete}/${id}`, {
                headers: { Authorization: `Bearer ${user.token}` },
              });
              if (data.status === 200 && data.errorCode === 0) {
                showToast("Success", "Placement test deleted successfully.", "success");
                setReFetchData(true);
              } else {
                showToast("Error", data?.message || "Failed to delete placement test.", "error");
              }
            } catch (error) {
              showToast("Error", error.response?.data?.message || "Failed to delete placement test.", "error");
            }
          }
        },
      });
    }

    return actions;
  };

  const rows = Array.isArray(placementTests) ? placementTests : [];

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
              Placement Tests
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage and enroll in placement tests.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              onClick={() => setReFetchData(true)}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            {hasPermission(user, "create_placement_test") && (
              <button
                onClick={() => navigate('/dashboard/placement-tests/create')}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-primary-hover transition-colors"
              >
                <span>New Test</span>
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-t border-gray-100 pt-4">
          <div className="flex space-x-4 overflow-x-auto">
            {determinedTableTabs.map((tab) => (
              <button
                key={tab}
                className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${activeTab === tab ? "bg-blue-50 text-blue-700" : "text-gray-500 hover:bg-gray-50"
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-56">
            <input
              type="text"
              placeholder="Search..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
            />
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column, idx) => (
                    <th
                      key={idx}
                      className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide border-b border-gray-100 ${column.sortable ? "cursor-pointer hover:bg-gray-100" : ""}`}
                      onClick={() => column.sortable && handleSortClick(column.id)}
                    >
                      <div className="flex items-center gap-1">
                        <span>{column.label}</span>
                        {column.sortable && renderSortIndicator(column.id)}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loader ? (
                  <tr><td colSpan={columns.length + 1} className="py-10 text-center"><Loader /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={columns.length + 1} className="py-10 text-center text-gray-500">No tests found</td></tr>
                ) : (
                  rows.map((test, index) => (
                    <tr key={index} className="group hover:bg-gray-50 transition-colors text-sm cursor-pointer" onClick={() => navigate(`/dashboard/placement-tests/${test.id}`)}>
                      <td className="px-4 sm:px-6 py-4 font-medium text-gray-900 group-hover:text-primary transition-colors">{test.title}</td>
                      <td className="px-4 sm:px-6 py-4 text-gray-700">{test.price}</td>
                      <td className="px-4 sm:px-6 py-4 text-gray-700">{new Date(test.start_date).toLocaleDateString()}</td>
                      <td className="px-4 sm:px-6 py-4 text-gray-700">{test.duration_minutes} min</td>
                      <td className="px-4 sm:px-6 py-4">
                        <div className="flex items-center gap-2">
                          <img src={test.status === 'published' ? BlueDot : OrangeDot} alt="" className="w-2 h-2" />
                          <span className="capitalize">{test.status}</span>
                        </div>
                      </td>
                      {activeTab === "My Tests" && (
                        <>
                          <td className="px-4 sm:px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${test.pivot?.approval_status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                              {test.pivot?.approval_status}
                            </span>
                          </td>
                          <td className="px-4 sm:px-6 py-4">{test.pivot?.payment_status}</td>
                          <td className="px-4 sm:px-6 py-4 font-bold">{test.pivot?.score ?? '-'}</td>
                        </>
                      )}
                      <td className="px-4 sm:px-6 py-4 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                        {activeTab === "All Tests" && user.student && !test.is_enrolled && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/placement-tests/${test.id}`);
                            }}
                            className="text-primary hover:underline font-semibold"
                          >
                            Enroll
                          </button>
                        )}
                        {activeTab === "My Tests" && test.pivot?.approval_status === "approved" && !test.pivot?.score && (
                          <a
                            href={`${API.examsAppUrl}/exam/${test.exam?.exam_code}?token=${user.token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-primary text-white px-4 py-1.5 rounded-lg text-xs font-semibold hover:bg-primary-hover transition-colors"
                          >
                            Take Test
                          </a>
                        )}
                        <ActionsDropdown actions={getDropdownActions(test.id, test)} />
                      </td>

                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      </div>
    </div>
  );
};

export default PlacementTestTable;
